import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Slide,
  Container,
  Chip,
  Avatar,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Settings,
  Folder,
  ArrowBack,
  Storage,
} from '@mui/icons-material';
import { Project } from '../types';
import { currentChangesAPI, gitLogAPI, projectsAPI } from '../services/api';

interface ServerDashboardProps {
  server: Project;
  onBack: () => void;
  onNavigateToParameters: () => void;
  onNavigateToApplicationFolders: () => void;
  onNavigateToCurrentChanges: () => void;
}

const ServerDashboard: React.FC<ServerDashboardProps> = ({
  server,
  onBack,
  onNavigateToParameters,
  onNavigateToApplicationFolders,
  onNavigateToCurrentChanges,
}) => {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState({
    currentChanges: {
      totalFiles: 0,
      modified: 0,
      newFiles: 0
    },
    applicationFolders: {
      appFolders: 0,
      activeVersions: 0
    }
  });
  const [loading, setLoading] = useState(true);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      // Check if projectName is an IP address (for server projects)
      const isIpAddress = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$|^::1$|^localhost$/.test(server.projectName);

      let currentChangesResponse;
      let appFoldersResponse;

      if (isIpAddress) {
        // For server IP addresses, use the current changes API to get critical files with isRobot=false
        currentChangesResponse = await currentChangesAPI.getCurrentChangesByIpAddress(server.projectName, false);
        // For application folders, we need to get projects first, then aggregate all folders
        const projectsResponse = await projectsAPI.getProjectsByIpAddress(server.projectName, false);
        if (projectsResponse.success && projectsResponse.data.length > 0) {
          // Aggregate application folders from all projects
          const allFolders = [];
          for (const project of projectsResponse.data) {
            try {
              const projectFoldersResponse = await gitLogAPI.getApplicationFolders(project.projectName);
              if (projectFoldersResponse.success && projectFoldersResponse.data.folders) {
                allFolders.push(...projectFoldersResponse.data.folders);
              }
            } catch (error) {
              console.warn(`Failed to fetch folders for project ${project.projectName}:`, error);
            }
          }
          appFoldersResponse = {
            success: true,
            data: {
              folders: allFolders,
              summary: {
                totalFolders: projectsResponse.data.length, // Show number of projects instead of total folders
                activeFolders: allFolders.filter(f => f.isActive).length
              }
            }
          };
        } else {
          appFoldersResponse = { success: true, data: { folders: [] } };
        }
      } else {
        // For regular project names, use the standard APIs
        currentChangesResponse = await currentChangesAPI.getCurrentChanges(server.projectName, {
          branchName: 'main'
        });
        appFoldersResponse = await gitLogAPI.getApplicationFolders(server.projectName);
      }

      // Process current changes data
      let currentChangesData = {
        totalFiles: 0,
        modified: 0,
        newFiles: 0
      };

      if (currentChangesResponse.success && currentChangesResponse.data) {
        if (isIpAddress) {
          // For IP-based current changes API response
          const data = currentChangesResponse.data;
          let allFiles: any[] = [];
          
          // The getCurrentChangesByIpAddress returns data with allFiles array
          if (data.allFiles && Array.isArray(data.allFiles)) {
            allFiles = data.allFiles;
          } else if (Array.isArray(data)) {
            // If data is directly an array, use it
            allFiles = data;
          }
          
          currentChangesData = {
            totalFiles: allFiles.length,
            modified: allFiles.filter((file: any) => file.changeType === 'modified').length,
            newFiles: allFiles.filter((file: any) => file.changeType === 'added' || file.changeType === 'new').length
          };
        } else {
          // For regular project response, use changes array
          const changes = currentChangesResponse.data.changes || [];
          currentChangesData = {
            totalFiles: changes.length,
            modified: changes.filter((change: any) => change.changeType === 'modified').length,
            newFiles: changes.filter((change: any) => change.changeType === 'added').length
          };
        }
      }

      // Process application folders data
      let appFoldersData = {
        appFolders: 0,
        activeVersions: 0
      };

      if (appFoldersResponse.success && appFoldersResponse.data) {
        if (isIpAddress) {
          // For servers, show the number of projects as applications
          // Use the summary.totalFolders which contains the project count
          appFoldersData = {
            appFolders: appFoldersResponse.data.summary?.totalFolders || 0,
            activeVersions: 0 // Not needed for the simplified display
          };
        } else {
          // For regular projects, show the number of folders
          appFoldersData = {
            appFolders: appFoldersResponse.data.folders?.length || 0,
            activeVersions: appFoldersResponse.data.folders?.filter((folder: any) => folder.isActive).length || 0
          };
        }
      }

      setDashboardData({
        currentChanges: currentChangesData,
        applicationFolders: appFoldersData
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [server.projectName]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const renderOptionCard = (
    title: string,
    description: string,
    icon: React.ReactNode,
    onClick: () => void,
    delay: number,
    cardKey: string,
    counts?: { label: string; value: number; color?: string }[]
  ) => (
    <Slide direction="up" in timeout={800 + delay} mountOnEnter unmountOnExit>
      <Card
        sx={{
          height: 280,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 4,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          transform: hoveredCard === cardKey ? 'translateY(-8px) scale(1.02)' : 'translateY(0)',
          boxShadow: hoveredCard === cardKey
            ? '0 20px 40px rgba(0,0,0,0.15)'
            : '0 8px 32px rgba(0,0,0,0.1)',
          '&:hover': {
            background: 'rgba(255, 255, 255, 1)',
          },
        }}
        onClick={onClick}
        onMouseEnter={() => setHoveredCard(cardKey)}
        onMouseLeave={() => setHoveredCard(null)}
      >
        <CardContent sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          textAlign: 'center'
        }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              mb: 3,
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)',
              transform: hoveredCard === cardKey ? 'scale(1.1)' : 'scale(1)',
              transition: 'all 0.3s ease',
            }}
          >
            {icon}
          </Avatar>

          <Typography variant="h5" fontWeight="bold" sx={{ color: '#1a202c', mb: 2 }}>
            {title}
          </Typography>

          <Typography variant="body2" sx={{ color: '#4a5568', mb: counts ? 3 : 0, lineHeight: 1.6 }}>
            {description}
          </Typography>

          {counts && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
              {counts.map((count, index) => (
                <Chip
                  key={index}
                  label={`${count.label}: ${count.value}`}
                  size="small"
                  sx={{
                    backgroundColor: count.color || 'rgba(71, 85, 105, 0.1)',
                    color: count.color ? 'white' : '#475569',
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                  }}
                />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Slide>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress sx={{ color: 'white', mb: 2 }} />
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
          Loading dashboard data...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={onBack}
          sx={{
            color: 'rgba(255, 255, 255, 0.8)',
            mb: 2,
            '&:hover': {
              color: 'white',
              background: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          Back to Server Options
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Avatar
            sx={{
              width: { xs: 48, sm: 56, md: 60 },
              height: { xs: 48, sm: 56, md: 60 },
              mr: { xs: 0, sm: 2, md: 3 },
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            <Storage sx={{ fontSize: 30, color: 'white' }} />
          </Avatar>
          <Box>
            <Typography variant="h3" fontWeight="bold" sx={{ color: 'white', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' } }}>
              {server.customName || server.projectName}
            </Typography>
            <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: { xs: '0.95rem', md: '1.125rem' } }}>
              Server Configuration & Monitoring
            </Typography>
            {server.ipAddress && (
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 0.5, fontSize: { xs: '0.85rem', md: '1rem' } }}>
                IP Address: {server.ipAddress}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)' }} />
      </Box>

      {/* Cards Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr', md: 'repeat(2, 1fr)' },
          gap: { xs: 2, md: 4 },
          maxWidth: 800,
          mx: 'auto',
        }}
      >
        {/* Card 1: Current Changes/Deviation from Verified Parameters */}
        {renderOptionCard(
          'Critical Files',
          'View all critical files with their status and modifications',
          <Settings sx={{ fontSize: 36, color: 'white' }} />,
          onNavigateToCurrentChanges,
          0,
          'current-changes',
          [
            { label: 'Total Files', value: dashboardData.currentChanges.totalFiles, color: '#22c55e' },
            { label: 'Modified', value: dashboardData.currentChanges.modified, color: '#f59e0b' },
            { label: 'New Files', value: dashboardData.currentChanges.newFiles, color: '#3b82f6' }
          ]
        )}

        {/* Card 2: Change History of Application Folders */}
        {renderOptionCard(
          'Application Folders',
          'Change history of application folders - ensure correct versions are running',
          <Folder sx={{ fontSize: 36, color: 'white' }} />,
          onNavigateToApplicationFolders,
          200,
          'app-folders',
          [
            { label: 'Total Applications', value: dashboardData.applicationFolders.appFolders, color: '#10b981' }
          ]
        )}
      </Box>

      {/* Additional Info */}
      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
          Monitor server configuration and application versions to ensure system integrity
        </Typography>
      </Box>
    </Container>
  );
};

export default ServerDashboard;
